import type { JsonValue, TgdBootstrap, TgdPublicApi } from '../../tgd/contract/index.js';

type Subscriber = { key: string; cb: (variant: string | null) => void };

/** Lazy facade. Only the shared tgd runtime evaluates assignments and emits exposures. */
export class ExperimentsModule {
  private runtime: TgdPublicApi | null = null;
  private loading: Promise<void> | null = null;
  private settled = false;
  private readonly reads = new Set<string>();
  private readonly subscribers: Subscriber[] = [];

  constructor(private readonly core: () => Promise<TgdPublicApi | null>, private readonly bootstrap?: TgdBootstrap) {}

  private variant(key: string): string | null {
    return this.bootstrap?.experiments[key]?.arm ?? this.bootstrap?.flags[key]?.variant ?? null;
  }

  private start(): Promise<void> {
    if (typeof window === 'undefined') return Promise.resolve();
    this.loading ??= (async () => {
      try { this.runtime = await this.core(); } catch { /* Fall back to bootstrap. */ }
      this.settled = true;
      const runtime = this.runtime;
      for (const key of this.reads) {
        try { runtime?.getVariant(key); } catch { /* Runtime failures cannot interrupt the merchant. */ }
      }
      this.reads.clear();
      for (const { key, cb } of this.subscribers.splice(0)) {
        try { if (runtime) runtime.onVariant(key, cb); else cb(this.variant(key)); } catch { /* Isolate subscribers. */ }
      }
      try { await runtime?.ready(); } catch { /* Offline assignment is a settled facade. */ }
    })();
    return this.loading;
  }

  private read(key: string): void {
    if (!this.settled) this.reads.add(key);
    void this.start();
  }

  getVariant(key: string): string | null {
    if (this.runtime) return this.runtime.getVariant(key);
    this.read(key);
    return this.variant(key);
  }

  getPayload(key: string): JsonValue | null {
    if (this.runtime) return this.runtime.getPayload(key);
    this.read(key);
    return this.bootstrap?.experiments[key]?.payload ?? this.bootstrap?.flags[key]?.payload ?? null;
  }

  isEnabled(flagKey: string): boolean {
    if (this.runtime) return this.runtime.isEnabled(flagKey);
    this.read(flagKey);
    return this.bootstrap?.flags[flagKey]?.enabled ?? false;
  }

  onVariant(key: string, cb: (variant: string | null) => void): void {
    if (this.runtime) { this.runtime.onVariant(key, cb); return; }
    if (this.settled) { cb(this.variant(key)); return; }
    this.subscribers.push({ key, cb });
    void this.start();
  }

  ready(): Promise<void> { return this.start(); }
}
